import { Button } from '@clef/client-library';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  OutlinedInput,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import { useSnackbar } from 'notistack';
import React, { useCallback, useMemo, useState } from 'react';

import MatTable from '@/components/Utils/MatTable';
import { projectQueryKeys, useGetOrgAdminProjectDetailsQuery } from '@/serverStore/projects';
import { transferProjectOwnershipApi, updateUserPermissionsApi } from '@/hooks/api/useOrgAdminApi';
import { useTypedSelector } from '@/hooks/useTypedSelector';
import { Project, ProjectRole, UserId, UserStatus, UserWithUserProject } from '@clef/shared/types';
import UserProfile, { DataItem, getUserName } from '../components/UserProfile';
import { isUser } from '@clef/shared/utils/user';
import { toTitle } from '@/utils';
import { Column } from '@material-table/core';
import { useQueryClient } from '@tanstack/react-query';
import { useAppDispatch } from '../../../store';
import { fetchUsers } from '../../../store/user_store';
import { Chip } from '../components/Chip';
import { tableStyleOptions } from '../styles';

const useStyles = makeStyles(theme => ({
  dialogContent: {
    marginBottom: theme.spacing(5),
  },
  searchIcon: {
    color: theme.palette.grey[500],
  },
  projectGridSearchInput: {
    padding: '9px 0 9px 14px',
  },
  projectGridSearchContainer: {
    borderRadius: 6,
    minWidth: 280,
    marginRight: 20,
    height: 34,
    backgroundColor: theme.palette.common.white,
  },
  moreVertIconButton: {
    maxWidth: 100,
  },
}));
const getUserStatus = (item: DataItem) =>
  isUser(item) ? toTitle(item.status || 'Unknown') : toTitle(UserStatus.Pending);

interface ChangeOwnershipDialogProps {
  open: boolean;
  project: Project;
  onClose: () => void;
}

type SearchBarProps = {
  searchText: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
};
const SearchBar = (props: SearchBarProps) => {
  const styles = useStyles();
  const { searchText, onChange } = props;
  return (
    <OutlinedInput
      onClick={e => e.stopPropagation()}
      value={searchText}
      onChange={onChange}
      placeholder={t('Search member')}
      endAdornment={<SearchIcon className={styles.searchIcon} />}
      inputProps={{
        className: styles.projectGridSearchInput,
      }}
      className={styles.projectGridSearchContainer}
    />
  );
};

export const ChangeOwnershipDialog: React.FC<ChangeOwnershipDialogProps> = ({
  open,
  project,
  onClose,
}) => {
  const styles = useStyles();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [isRevoking, setIsRevoking] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<UserId>('');
  const allUsers = useTypedSelector(state => state.user.users);
  const queryClient = useQueryClient();

  const { data: projectWithUsers, isLoading: isQuerySelectedProjectLoading } =
    useGetOrgAdminProjectDetailsQuery(project.id ?? 0);
  const projectUsers = useMemo(() => projectWithUsers?.users ?? [], [projectWithUsers]);
  const projectUsersById = useMemo(() => {
    return projectUsers.reduce((acc, user) => {
      acc[user.id] = user;
      return acc;
    }, {} as Record<UserId, UserWithUserProject>);
  }, [projectUsers]);
  const projectOwner = projectUsers.find(user => user.userProject.role === ProjectRole.Owner);
  const users = allUsers;

  const renderedUsers = useMemo(
    () =>
      searchText
        ? users.filter(user => {
            return (
              (isUser(user) ? `${user.name} ${user.lastName}` : t('Pending user'))
                .toLowerCase()
                .includes(searchText.toLowerCase()) ||
              user.email.toLowerCase().includes(searchText.toLowerCase())
            );
          })
        : [...users],
    [searchText, users],
  );

  const columns: Column<DataItem>[] = useMemo(() => {
    return [
      { title: t('Id'), field: 'id', hidden: true },
      {
        title: '',
        width: 40,
        render: item => (
          <input
            type="radio"
            checked={item.id === selectedUserId}
            disabled={
              projectOwner?.id === item.id ||
              ('status' in item && item.status === UserStatus.Inactive)
            }
            onChange={() => {
              setSelectedUserId(item.id === selectedUserId ? '' : item.id.toString());
            }}
          />
        ),
        sorting: false,
      },
      {
        title: (
          <SearchBar
            searchText={searchText}
            onChange={({ target: { value } }) => {
              setSearchText(value);
            }}
          />
        ),
        render: item => <UserProfile item={item} showUserStatus="inactive-only" />,
        customSort: (a, b) => {
          const nameA = getUserName(a);
          const nameB = getUserName(b);
          return nameA.localeCompare(nameB);
        },
      },
      {
        title: t('Role'),
        width: 280,
        render: item => toTitle(projectUsersById[item.id]?.userProject.role || '-'),
        customSort: (a, b) => {
          const titleA = toTitle(projectUsersById[a.id]?.userProject.role || '-');
          const titleB = toTitle(projectUsersById[b.id]?.userProject.role || '-');
          return titleA.localeCompare(titleB);
        },
      },
      {
        title: t('Status'),
        width: 280,
        render: item =>
          isUser(item) ? (
            <Chip
              text={toTitle(item.status || 'Unknown')}
              color={item.status === UserStatus.Active ? 'green' : 'blue'}
            />
          ) : (
            <Chip text={toTitle(UserStatus.Pending)} color="blue" />
          ),
        customSort: (a, b) => {
          const statusA = getUserStatus(a);
          const statusB = getUserStatus(b);
          return statusA.localeCompare(statusB);
        },
      },
    ];
  }, [projectOwner?.id, projectUsersById, searchText, selectedUserId]);

  const handleTransferProject = useCallback(async () => {
    setIsRevoking(true);

    if (!project) {
      setIsRevoking(false);
      return;
    }

    const user = allUsers.find(user => user.id === selectedUserId);

    if (!user) {
      setIsRevoking(false);
      return;
    }

    // If the user is already the project owner, do nothing
    if (projectOwner?.id === user.id) {
      setIsRevoking(false);
      return;
    }

    // If user is not already in the project, we need to invite them first
    // before transferring ownership
    if (!projectUsers.some(projectUser => projectUser.id === user.id)) {
      await updateUserPermissionsApi(project.id, [
        {
          id: user.id,
          permissions: [],
        },
      ]);
    }

    await transferProjectOwnershipApi(project.orgId, project.id, selectedUserId)
      .then(() => {
        enqueueSnackbar(t('Successfully transferred ownership.'), {
          variant: 'success',
        });
        dispatch(fetchUsers());
        queryClient.refetchQueries(projectQueryKeys.list(project.orgId));
      })
      .catch(() => {
        enqueueSnackbar(t('Failed to transfer project ownership'), {
          variant: 'error',
        });
      });

    onClose();

    setIsRevoking(false);
  }, [
    project,
    allUsers,
    projectOwner?.id,
    projectUsers,
    selectedUserId,
    onClose,
    enqueueSnackbar,
    dispatch,
    queryClient,
  ]);

  if (isQuerySelectedProjectLoading) {
    return null;
  }

  return (
    <Dialog open={open} maxWidth="md">
      <DialogTitle>{t('Change Ownership')}</DialogTitle>
      <DialogContent className={styles.dialogContent}>
        <Box>
          {t('Who do you want to transfer ownership of {{projectName}} to?', {
            projectName: project?.name,
          })}
        </Box>
        <Box marginTop={8}>
          <MatTable<DataItem>
            title=""
            data={renderedUsers}
            columns={columns}
            options={{
              ...tableStyleOptions,
              search: false,
              toolbar: false,
              padding: 'dense',
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button id="cancel-revoke-invitation-button" color="primary" onClick={onClose}>
          {t('Cancel')}
        </Button>
        <Button
          type="submit"
          color="primary"
          variant="contained"
          disabled={isRevoking || selectedUserId == ''}
          onClick={handleTransferProject}
          id="confirm-revoke-invitation-button"
        >
          {isRevoking ? t('Transferring...') : t('Transfer')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
